/**
 * PreviewMetricsBody — weights / score / performance / reason / breakdown.
 *
 * Performance shows the raw stat fields (impressions, clicks, ROI, …) that
 * the optimizer actually saw for this item — surfaces gaps clearly so the
 * "all items at the same weight" mystery is never a mystery.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';

import { useOptionalFlowOptimizerLivePreview } from '@/features/flow-optimizer/context/FlowOptimizerLivePreviewContext';

import BreakdownList from './BreakdownList';
import { ScoreCalculationSection } from './calc';
import MetricContributionsTable from './MetricContributionsTable';
import PreviewPerformanceSection from './PreviewPerformanceSection';
import WhySameWeightCallout from './WhySameWeightCallout';
import { HoverMetricRow, HoverSection } from './hover-primitives';
import { ExclusionReasonSection, FinancialSummaryStrip } from './state';
import {
  deltaTone,
  fmtDelta,
  fmtPctFromPerMille,
  fmtPerMille,
} from '../format';
import type { PreviewRow } from '../../preview-row.types';

export interface PreviewMetricsBodyProps {
  row: PreviewRow;
}

const PreviewMetricsBody: FC<PreviewMetricsBodyProps> = ({ row }) => {
  const previewCtx = useOptionalFlowOptimizerLivePreview();
  const expectedRanges = useMemo(
    () => Array.from(new Set((row.breakdown ?? []).map((b) => b.dateRange))),
    [row.breakdown],
  );
  return (
    <div className="space-y-2.5 px-3 py-2.5 text-[11px]">
      <FinancialSummaryStrip row={row} subFormulas={previewCtx?.subFormulas} />
      <WeightsSection row={row} />
      <ScoreCalculationSection
        row={row}
        bounds={previewCtx?.bounds ?? null}
        previewItems={previewCtx?.result?.items ?? []}
        subFormulas={previewCtx?.subFormulas}
      />
      <PreviewPerformanceSection
        metricsByDateRange={row.metricsByDateRange}
        expectedDateRanges={expectedRanges}
      />
      <WhySameWeightCallout row={row} />
      <ExclusionReasonSection row={row} />
      {row.breakdown && row.breakdown.length > 0 ? (
        <HoverSection label="Per-group breakdown">
          <BreakdownList entries={row.breakdown} subFormulas={previewCtx?.subFormulas} />
        </HoverSection>
      ) : null}
      {row.breakdown && row.breakdown.length > 0 ? (
        <MetricContributionsTable entries={row.breakdown} />
      ) : null}
    </div>
  );
};

const WeightsSection: FC<{ row: PreviewRow }> = ({ row }) => (
  <HoverSection label="Weights">
    <HoverMetricRow
      label="Current"
      primary={fmtPctFromPerMille(row.currentPerMille)}
      secondary={fmtPerMille(row.currentPerMille)}
    />
    <HoverMetricRow
      label="Optimal"
      primary={fmtPctFromPerMille(row.optimalPerMille)}
      secondary={fmtPerMille(row.optimalPerMille)}
      emphasis={!row.passThrough}
    />
    <HoverMetricRow
      label="Change"
      primary={fmtDelta(row.deltaPerMille)}
      secondary={null}
      tone={deltaTone(row.deltaPerMille)}
    />
  </HoverSection>
);

export default PreviewMetricsBody;
